import React, { useState } from "react";

export type HealthMetricType = "blood_pressure" | "blood_glucose";

export interface HealthRecord {
  id: string;
  measurement_time: string;
  // Blood Pressure
  systolic?: number;
  diastolic?: number;
  pulse?: number;
  measurement_position?: string;
  body_position?: string;
  // Blood Glucose
  glucose_level?: number;
  meal_relation?: string;
  specimen_source?: string;
}

export interface AverageMetrics {
  week?: {
    systolic?: number;
    diastolic?: number;
    pulse?: number;
    glucose_level?: number;
  };
  month?: {
    systolic?: number;
    diastolic?: number;
    pulse?: number;
    glucose_level?: number;
  };
  threeMonth?: {
    systolic?: number;
    diastolic?: number;
    pulse?: number;
    glucose_level?: number;
  };
}

interface FormData {
  // Shared
  measurement_time: string;
  measurement_date: string;
  measurement_hour: string;
  measurement_minute: string;
  device_type: string;
  notes: string;
  // Blood Pressure
  systolic: string;
  diastolic: string;
  pulse: string;
  measurement_position: string;
  body_position: string;
  // Blood Glucose
  glucose_level: string;
  meal_relation: string;
  specimen_source: string;
}

interface HealthMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricType: HealthMetricType;
  latestRecord?: HealthRecord;
  averageMetrics?: AverageMetrics;
  history?: HealthRecord[];
  onSubmit: (data: any) => Promise<void>;
  isLoading?: boolean;
}

const getDefaultFormData = (): FormData => {
  const now = new Date();
  return {
    measurement_time: "",
    measurement_date: now.toISOString().split("T")[0],
    measurement_hour: String(now.getHours()).padStart(2, "0"),
    measurement_minute: String(now.getMinutes()).padStart(2, "0"),
    device_type: "manual",
    notes: "",
    systolic: "",
    diastolic: "",
    pulse: "",
    measurement_position: "right_upper_arm",
    body_position: "sitting",
    glucose_level: "",
    meal_relation: "general",
    specimen_source: "capillary_blood",
  };
};

export const HealthMetricsModal: React.FC<HealthMetricsModalProps> = React.memo(
  ({ isOpen, onClose, metricType, latestRecord, averageMetrics, history = [], onSubmit, isLoading = false }) => {
    const [formData, setFormData] = useState<FormData>(getDefaultFormData());
    const [error, setError] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");

    if (!isOpen) return null;

    const isBP = metricType === "blood_pressure";
    const title = isBP ? "혈압 관리" : "혈당 관리";

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setError("");
    };

    const validateForm = (): boolean => {
      if (isBP) {
        if (!formData.systolic.trim() || !formData.diastolic.trim()) {
          setError("수축기/이완기 혈압을 입력해주세요.");
          return false;
        }
        const sys = parseInt(formData.systolic);
        const dia = parseInt(formData.diastolic);
        if (isNaN(sys) || isNaN(dia) || sys < 50 || sys > 250 || dia < 30 || dia > 150) {
          setError("유효한 혈압 값을 입력해주세요. (수축기: 50-250, 이완기: 30-150)");
          return false;
        }
        if (sys <= dia) {
          setError("수축기 혈압이 이완기 혈압보다 커야 합니다.");
          return false;
        }
      } else {
        if (!formData.glucose_level.trim()) {
          setError("혈당 값을 입력해주세요.");
          return false;
        }
        const glucose = parseFloat(formData.glucose_level);
        if (isNaN(glucose) || glucose < 20 || glucose > 600) {
          setError("유효한 혈당 값을 입력해주세요. (20-600 mg/dL)");
          return false;
        }
      }
      if (!formData.measurement_date) {
        setError("측정 날짜를 선택해주세요.");
        return false;
      }
      return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      setSuccessMessage("");

      if (!validateForm()) return;

      try {
        // 로컬 시간을 문자열로 변환 (타임존 고려)
        const dateObj = new Date(
          `${formData.measurement_date}T${formData.measurement_hour}:${formData.measurement_minute}`
        );
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const measurement_time = new Date(dateObj.getTime() - tzOffset).toISOString();

        const submitData = {
          measurement_time,
          device_type: formData.device_type,
          notes: formData.notes,
          data_source: "manual",
          ...(isBP && {
            systolic: parseInt(formData.systolic),
            diastolic: parseInt(formData.diastolic),
            pulse: formData.pulse ? parseInt(formData.pulse) : null,
            measurement_position: formData.measurement_position,
            body_position: formData.body_position,
          }),
          ...(!isBP && {
            glucose_level: parseFloat(formData.glucose_level),
            meal_relation: formData.meal_relation,
            specimen_source: formData.specimen_source,
          }),
        };

        await onSubmit(submitData);
        setSuccessMessage(`${title} 정보가 등록되었습니다!`);
        setTimeout(() => {
          setFormData(getDefaultFormData());
          setSuccessMessage("");
          onClose();
        }, 1500);
      } catch (err) {
        setError(err instanceof Error ? err.message : "등록 중 오류가 발생했습니다.");
      }
    };

    const handleClose = () => {
      setFormData(getDefaultFormData());
      setError("");
      setSuccessMessage("");
      onClose();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-end bg-black bg-opacity-30 transition-all duration-300">
        {/* 모달 배경 */}
        <div
          className="absolute inset-0"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Escape" && handleClose()}
        />

        {/* 모달 컨텐츠 */}
        <div className="relative w-full bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[90vh] flex flex-col">
          {/* 헤더 */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <h2 className="text-[16px] font-bold text-slate-900">{title}</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="닫기"
            >
              ✕
            </button>
          </div>

          {/* 폼 - 스크롤 가능 */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* 에러 메시지 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-[12px] text-red-700 font-semibold">{error}</p>
              </div>
            )}

            {/* 성공 메시지 */}
            {successMessage && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-[12px] text-emerald-700 font-semibold">{successMessage}</p>
              </div>
            )}

            {/* 최근 기록 카드 */}
            {latestRecord && (
              <div className={`rounded-lg p-3 ${isBP ? 'bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200' : 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] text-slate-600 font-semibold">최근 기록</p>
                  <p className="text-[10px] text-slate-500">
                    {new Date(latestRecord.measurement_time).toLocaleDateString("ko-KR", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <div className="flex items-baseline gap-2">
                  {isBP ? (
                    <>
                      <span className="text-[24px] font-bold text-violet-700">{latestRecord.systolic}</span>
                      <span className="text-[18px] text-slate-400">/</span>
                      <span className="text-[24px] font-bold text-violet-700">{latestRecord.diastolic}</span>
                      <span className="text-[12px] text-slate-600 ml-2">mmHg</span>
                      {latestRecord.pulse && (
                        <span className="text-[12px] text-slate-600 ml-auto">맥박: {latestRecord.pulse} bpm</span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-[24px] font-bold text-blue-700">{latestRecord.glucose_level}</span>
                      <span className="text-[12px] text-slate-600">mg/dL</span>
                      <span className="text-[10px] text-slate-500 ml-auto">
                        {latestRecord.meal_relation === "fasting" && "공복"}
                        {latestRecord.meal_relation === "before_meal" && "식전"}
                        {latestRecord.meal_relation === "after_meal" && "식후"}
                        {latestRecord.meal_relation === "bedtime" && "자기 전"}
                        {latestRecord.meal_relation === "general" && "일반"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 분할선 */}
            <div className="border-t border-slate-200 pt-4">
              <p className="text-[11px] font-semibold text-slate-600 mb-3">새로운 기록 입력</p>
            </div>

            {/* 측정 날짜 & 시간 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                  측정 날짜 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="measurement_date"
                  value={formData.measurement_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                  측정 시간
                </label>
                <div className="flex gap-2">
                  <select
                    name="measurement_hour"
                    value={formData.measurement_hour}
                    onChange={handleChange}
                    className="flex-1 px-2 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    {Array.from({ length: 24 }, (_, i) =>
                      String(i).padStart(2, "0")
                    ).map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}시
                      </option>
                    ))}
                  </select>
                  <select
                    name="measurement_minute"
                    value={formData.measurement_minute}
                    onChange={handleChange}
                    className="flex-1 px-2 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    {Array.from({ length: 60 }, (_, i) =>
                      String(i).padStart(2, "0")
                    ).map((minute) => (
                      <option key={minute} value={minute}>
                        {minute}분
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 혈압 입력 필드 */}
            {isBP && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                      수축기 혈압 (mmHg) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="systolic"
                      value={formData.systolic}
                      onChange={handleChange}
                      placeholder="예: 120"
                      min="50"
                      max="250"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                      이완기 혈압 (mmHg) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="diastolic"
                      value={formData.diastolic}
                      onChange={handleChange}
                      placeholder="예: 80"
                      min="30"
                      max="150"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                    맥박 (bpm)
                  </label>
                  <input
                    type="number"
                    name="pulse"
                    value={formData.pulse}
                    onChange={handleChange}
                    placeholder="예: 70"
                    min="30"
                    max="200"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                      측정 위치
                    </label>
                    <select
                      name="measurement_position"
                      value={formData.measurement_position}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="left_wrist">왼쪽 손목</option>
                      <option value="right_wrist">오른쪽 손목</option>
                      <option value="left_upper_arm">왼쪽 팔</option>
                      <option value="right_upper_arm">오른쪽 팔</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                      신체 자세
                    </label>
                    <select
                      name="body_position"
                      value={formData.body_position}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="sitting">앉은 자세</option>
                      <option value="standing">서 있는 자세</option>
                      <option value="lying">누운 자세</option>
                      <option value="reclining">반쯤 누운 자세</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* 혈당 입력 필드 */}
            {!isBP && (
              <>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                    혈당 (mg/dL) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="glucose_level"
                    value={formData.glucose_level}
                    onChange={handleChange}
                    placeholder="예: 105.5"
                    step="0.1"
                    min="20"
                    max="600"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                      식사 관계
                    </label>
                    <select
                      name="meal_relation"
                      value={formData.meal_relation}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="fasting">공복</option>
                      <option value="before_meal">식전</option>
                      <option value="after_meal">식후</option>
                      <option value="bedtime">자기 전</option>
                      <option value="general">일반</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                      검체 출처
                    </label>
                    <select
                      name="specimen_source"
                      value={formData.specimen_source}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="capillary_blood">모세혈</option>
                      <option value="interstitial_fluid">간질액</option>
                      <option value="plasma">혈장</option>
                      <option value="serum">혈청</option>
                      <option value="whole_blood">전혈</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* 기기 타입 */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-1">기기 타입</label>
              <input
                type="text"
                name="device_type"
                value={formData.device_type}
                onChange={handleChange}
                placeholder="예: Omron BP742N"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            {/* 메모 */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-1">메모</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="측정 시 특이사항이나 기타 정보를 기록하세요"
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              />
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 font-semibold text-[13px] hover:bg-slate-50 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                취소
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 rounded-lg bg-violet-600 text-white font-semibold text-[13px] hover:bg-violet-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? "등록 중..." : `${title} 등록`}
              </button>
            </div>
          </form>

        </div>
      </div>
    );
  }
);

HealthMetricsModal.displayName = "HealthMetricsModal";
